import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Palette, Check, Moon, Sun, Monitor, Type, Mail, Save, Clock, Upload, Tag, Sliders, Download } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { setMailTemplate, updateSettings } from '../store/slices/userSlice';
import getSettings from '../api/GetSettings';
import updateSettingsAPI from '../api/UpdateSettings';
import uploadLogo from '../api/UploadLogo';

const THEME_COLORS = [
    { name: 'Bleu Océan', value: '217.2 91.2% 59.8%', class: 'bg-blue-500' },
    { name: 'Violet Royal', value: '270 95% 60%', class: 'bg-purple-500' },
    { name: 'Émeraude', value: '142 71% 45%', class: 'bg-green-500' },
    { name: 'Orange Vif', value: '24 95% 53%', class: 'bg-orange-500' },
    { name: 'Rose Passion', value: '330 81% 60%', class: 'bg-pink-500' },
    { name: 'Rouge Rubis', value: '0 84% 60%', class: 'bg-red-500' },
];

const BACKGROUND_THEMES = [
    { name: 'Ardoise Classique', value: '222.2 84% 4.9%', preview: 'bg-slate-950' },
    { name: 'Bleu Nuit', value: '220 40% 6%', preview: 'bg-[#0a0e1a]' },
    { name: 'Ardoise Claire', value: '215 25% 15%', preview: 'bg-slate-800' },
    { name: 'Bleu Océan', value: '215 30% 20%', preview: 'bg-blue-900' },
];

// Helper to convert HEX to HSL format used in CSS variables
const hexToHSL = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
        r = parseInt(hex[1] + hex[2], 16);
        g = parseInt(hex[3] + hex[4], 16);
        b = parseInt(hex[5] + hex[6], 16);
    }
    r /= 255; g /= 255; b /= 255;
    let cmin = Math.min(r, g, b), cmax = Math.max(r, g, b), delta = cmax - cmin, h = 0, s = 0, l = 0;
    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
    l = (cmax + cmin) / 2;
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);
    return `${h} ${s}% ${l}%`;
};

// Helper: HSL string to HEX
const hslToHex = (hslStr) => {
    if (!hslStr) return "#000000";
    const parts = hslStr.split(' ');
    if (parts.length < 3) return "#000000";
    let h = parseFloat(parts[0]);
    let s = parseFloat(parts[1]) / 100;
    let l = parseFloat(parts[2]) / 100;

    let c = (1 - Math.abs(2 * l - 1)) * s,
        x = c * (1 - Math.abs((h / 60) % 2 - 1)),
        m = l - c / 2,
        r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) { r = c; g = x; b = 0; }
    else if (60 <= h && h < 120) { r = x; g = c; b = 0; }
    else if (120 <= h && h < 180) { r = 0; g = c; b = x; }
    else if (180 <= h && h < 240) { r = 0; g = x; b = c; }
    else if (240 <= h && h < 300) { r = x; g = 0; b = c; }
    else if (300 <= h && h < 360) { r = c; g = 0; b = x; }

    const toHex = n => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return "#" + toHex(r) + toHex(g) + toHex(b);
};

export default function Settings() {
    const dispatch = useDispatch();
    const mailTemplate = useSelector(state => state.user.lateReturnEmailTemplate);
    const [activeColor, setActiveColor] = useState(THEME_COLORS[0].value);
    const [activeBackground, setActiveBackground] = useState(BACKGROUND_THEMES[0].value);

    const [activeForeground, setActiveForeground] = useState('210 40% 98%');
    const [activeMuted, setActiveMuted] = useState('217.2 32.6% 17.5%'); // Default muted color
    const [themeMode, setThemeMode] = useState('dark');
    const [emailContent, setEmailContent] = useState(mailTemplate);
    const [loanDelay, setLoanDelay] = useState(useSelector(state => state.user.delaiEmprunt));
    const API_BASE = useSelector(state => state.user.apiBase);
    const token = useSelector(state => state.user.currentUser?.token);
    const code_etablissement = useSelector(state => state.user.currentUser?.code_etablissement);

    // Library Info State
    const {
        libraryAddress: reduxAddress,
        libraryEmail: reduxEmail,
        libraryPhone: reduxPhone,
        libraryHours: reduxHours,
        libraryLogo: reduxLogo,
        enableReservation: reduxEnableReservation
    } = useSelector(state => state.user);

    const [libAddress, setLibAddress] = useState(reduxAddress);
    const [libEmail, setLibEmail] = useState(reduxEmail);
    const [libPhone, setLibPhone] = useState(reduxPhone);
    const [libHours, setLibHours] = useState(JSON.parse(reduxHours));
    const [libLogo, setLibLogo] = useState(reduxLogo);
    const [enableReservation, setEnableReservation] = useState(reduxEnableReservation !== false);
    const fileInputRef = useRef(null);

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const result = await uploadLogo(API_BASE, token, file, code_etablissement);
            // The result usually contains the new URL or we constructed it in userSlice
            // Based on user's recent change in userSlice, the path is predictable
            const newLogoPath = `/uploads/logos/logo_${code_etablissement}.png?t=${Date.now()}`;
            setLibLogo(newLogoPath);
            dispatch(updateSettings({ libraryLogo: newLogoPath }));
            alert("Logo mis à jour avec succès !");
        } catch (error) {
            console.error("Upload failed:", error);
            alert("Erreur lors de l'upload du logo.");
        }
    };

    useEffect(() => {
        const savedColor = localStorage.getItem('theme-primary');
        const savedBackground = localStorage.getItem('theme-background');

        const savedForeground = localStorage.getItem('theme-foreground');
        const savedMuted = localStorage.getItem('theme-muted');
        const savedMode = localStorage.getItem('theme-mode') || 'dark';

        if (savedColor) {
            setActiveColor(savedColor);
            document.documentElement.style.setProperty('--primary', savedColor);
        }
        if (savedBackground) {
            setActiveBackground(savedBackground);
            document.documentElement.style.setProperty('--background', savedBackground);
            document.documentElement.style.setProperty('--card', savedBackground);
            document.documentElement.style.setProperty('--popover', savedBackground);
        }
        if (savedForeground) {
            setActiveForeground(savedForeground);
            document.documentElement.style.setProperty('--foreground', savedForeground);
        }
        if (savedMuted) {
            setActiveMuted(savedMuted);
            document.documentElement.style.setProperty('--muted', savedMuted);
        }

        setThemeMode(savedMode);
        if (savedMode === 'light') {
            document.documentElement.classList.add('light');
        } else {
            document.documentElement.classList.remove('light');
        }
    }, []);

    useEffect(() => {
        if (API_BASE && token && code_etablissement) {
            const fetchSettings = async () => {
                try {
                    const settingsList = await getSettings(API_BASE, token, code_etablissement);
                    if (settingsList && Array.isArray(settingsList)) {
                        const templateSetting = settingsList.find(s => s.nom === 'late-return-email-template');
                        const delaySetting = settingsList.find(s => s.nom === 'delai-emprunt');

                        if (templateSetting) {
                            setEmailContent(templateSetting.valeur);
                            dispatch(setMailTemplate(templateSetting.valeur));
                        }
                        if (delaySetting) {
                            const delay = parseInt(delaySetting.valeur, 10);
                            setLoanDelay(delay);
                            dispatch(updateSettings({ delaiEmprunt: delay }));
                        }

                        // Library Info Settings
                        const addressSetting = settingsList.find(s => s.nom === 'library-address');
                        const emailSetting = settingsList.find(s => s.nom === 'library-email');
                        const phoneSetting = settingsList.find(s => s.nom === 'library-phone');
                        const hoursSetting = settingsList.find(s => s.nom === 'library-hours');
                        //const logoSetting = settingsList.find(s => s.nom === 'library-logo');
                        const reservationSetting = settingsList.find(s => s.nom === 'enable-reservation');

                        if (addressSetting) { setLibAddress(addressSetting.valeur); dispatch(updateSettings({ libraryAddress: addressSetting.valeur })); }
                        if (emailSetting) { setLibEmail(emailSetting.valeur); dispatch(updateSettings({ libraryEmail: emailSetting.valeur })); }
                        if (phoneSetting) { setLibPhone(phoneSetting.valeur); dispatch(updateSettings({ libraryPhone: phoneSetting.valeur })); }
                        if (hoursSetting) { setLibHours(JSON.parse(hoursSetting.valeur)); dispatch(updateSettings({ libraryHours: hoursSetting.valeur })); }
                        if (reservationSetting) {
                            const isEnabled = reservationSetting.valeur === 'true';
                            setEnableReservation(isEnabled);
                            dispatch(updateSettings({ enableReservation: isEnabled }));
                        }
                        //if (logoSetting) { setLibLogo(logoSetting.valeur); dispatch(updateSettings({ libraryLogo: logoSetting.valeur })); }
                    }
                } catch (error) {
                    console.error("Failed to fetch settings from backend:", error);
                }
            };
            fetchSettings();
        }
    }, [API_BASE, token, code_etablissement, dispatch]);

    const handleColorChange = (value) => {
        setActiveColor(value);
        document.documentElement.style.setProperty('--primary', value);
        localStorage.setItem('theme-primary', value);
    };

    const handleBackgroundChange = (value) => {
        setActiveBackground(value);
        document.documentElement.style.setProperty('--background', value);
        document.documentElement.style.setProperty('--card', value);
        document.documentElement.style.setProperty('--popover', value);
        localStorage.setItem('theme-background', value);
    };

    const handleForegroundChange = (value) => {
        setActiveForeground(value);
        document.documentElement.style.setProperty('--foreground', value);
        localStorage.setItem('theme-foreground', value);
    };

    const handleMutedChange = (value) => {
        setActiveMuted(value);
        document.documentElement.style.setProperty('--muted', value);
        localStorage.setItem('theme-muted', value);
    };

    const handleThemeModeChange = (mode) => {
        setThemeMode(mode);
        localStorage.setItem('theme-mode', mode);
        if (mode === 'light') document.documentElement.classList.add('light');
        else document.documentElement.classList.remove('light');
    };

    const handleSaveSettings = async () => {
        dispatch(setMailTemplate(emailContent));
        dispatch(updateSettings({ delaiEmprunt: loanDelay }));
        //console.log(emailContent, loanDelay);
        if (API_BASE && token && code_etablissement) {
            try {
                await updateSettingsAPI(API_BASE, token, {
                    nom: 'late-return-email-template',
                    valeur: emailContent,
                    code_etablissement: code_etablissement
                });
                await updateSettingsAPI(API_BASE, token, {
                    nom: 'delai-emprunt',
                    valeur: loanDelay.toString(),
                    code_etablissement: code_etablissement
                });

                // Save Library Info
                await updateSettingsAPI(API_BASE, token, { nom: 'library-address', valeur: libAddress, code_etablissement });
                await updateSettingsAPI(API_BASE, token, { nom: 'library-email', valeur: libEmail, code_etablissement });
                await updateSettingsAPI(API_BASE, token, { nom: 'library-phone', valeur: libPhone, code_etablissement });
                await updateSettingsAPI(API_BASE, token, { nom: 'library-hours', valeur: JSON.stringify(libHours), code_etablissement });
                await updateSettingsAPI(API_BASE, token, { nom: 'enable-reservation', valeur: enableReservation.toString(), code_etablissement });
                // await updateSettingsAPI(API_BASE, token, { nom: 'library-logo', valeur: libLogo, code_etablissement });

                dispatch(updateSettings({
                    libraryAddress: libAddress,
                    libraryEmail: libEmail,
                    libraryPhone: libPhone,
                    libraryHours: JSON.stringify(libHours),
                    libraryLogo: libLogo,
                    enableReservation
                }));

                alert("Paramètres sauvegardés avec succès !");
            } catch (error) {
                console.error("Failed to save settings to backend:", error);
                alert("Erreur lors de la sauvegarde sur le serveur.");
            }
        }
    };


    const handleResetDefaults = () => {
        const defaultPrimary = THEME_COLORS[0].value;
        const defaultBackground = BACKGROUND_THEMES[0].value;
        const defaultForeground = '210 40% 98%';
        const defaultMuted = '217.2 32.6% 17.5%';
        const defaultMode = 'dark';

        handleColorChange(defaultPrimary);
        handleBackgroundChange(defaultBackground);
        handleForegroundChange(defaultForeground);
        handleMutedChange(defaultMuted);
        handleThemeModeChange(defaultMode);
    };

    const handleDownloadTemplate = () => {
        const headers = "nom;prénom;email;role;tel;classe\n";
        const example_etudiant = "DURAND;Paul;paul.durand@test.ch;Étudiant;0123456789;3ème B";
        const example_enseignant = "DUPONT;Alice;alice.dupont@test.ch;Enseignant;0123456789;;";
        const csvContent = headers + example_etudiant + "\n" + example_enseignant;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "template_import_membres.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-8 max-w-4xl mx-auto pb-20">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-8"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Paramètres</h1>
                        <p className="text-slate-400">Personnalisez votre expérience Biblius.</p>
                    </div>
                    <button
                        onClick={handleResetDefaults}
                        className="px-4 py-2 bg-slate-800 text-white rounded-lg border border-white/10 hover:bg-slate-700 transition-colors text-sm font-medium"
                    >
                        Paramètres par défaut
                    </button>
                </div>

                {/* Theme Colors Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            <Palette size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Personnalisation des couleurs</h2>
                            <p className="text-sm text-slate-400">Ajustez les couleurs de votre interface</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Primary Color */}
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">Couleur primaire</label>
                            <div className="flex flex-wrap gap-4 items-center">
                                {THEME_COLORS.map((color) => (
                                    <button
                                        key={color.name}
                                        onClick={() => handleColorChange(color.value)}
                                        className={`w-12 h-12 rounded-full ${color.class} shadow-lg flex items-center justify-center transition-all hover:scale-110 ${activeColor === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}
                                    >
                                        {activeColor === color.value && <Check size={20} className="text-white" />}
                                    </button>
                                ))}
                                <div className="flex items-center gap-3 ml-2 border-l border-white/10 pl-4">
                                    <input
                                        type="color"
                                        value={hslToHex(activeColor)}
                                        onChange={(e) => handleColorChange(hexToHSL(e.target.value))}
                                        className="w-10 h-10 bg-transparent cursor-pointer rounded overflow-hidden p-0 border-none"
                                    />
                                    <span className="text-xs text-slate-500 font-mono uppercase">{hslToHex(activeColor)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Background Color */}
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">Couleur de fond</label>
                            <div className="flex flex-wrap gap-4 items-center">
                                {BACKGROUND_THEMES.map((theme) => (
                                    <button
                                        key={theme.name}
                                        onClick={() => handleBackgroundChange(theme.value)}
                                        className={`w-12 h-12 rounded-xl ${theme.preview} border border-white/10 shadow-lg flex items-center justify-center transition-all hover:scale-110 ${activeBackground === theme.value ? 'ring-2 ring-primary' : ''}`}
                                    >
                                        {activeBackground === theme.value && <Check size={20} className="text-white" />}
                                    </button>
                                ))}
                                <div className="flex items-center gap-3 ml-2 border-l border-white/10 pl-4">
                                    <input
                                        type="color"
                                        value={hslToHex(activeBackground)}
                                        onChange={(e) => handleBackgroundChange(hexToHSL(e.target.value))}
                                        className="w-10 h-10 bg-transparent cursor-pointer rounded overflow-hidden p-0 border-none"
                                    />
                                    <span className="text-xs text-slate-500 font-mono uppercase">{hslToHex(activeBackground)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Font Color */}
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">Couleur de la police (Texte principal)</label>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-xl border border-white/10">
                                    <input
                                        type="color"
                                        value={hslToHex(activeForeground)}
                                        onChange={(e) => handleForegroundChange(hexToHSL(e.target.value))}
                                        className="w-10 h-10 bg-transparent cursor-pointer rounded overflow-hidden p-0 border-none"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-white">Couleur personnalisée</span>
                                        <span className="text-xs text-slate-500 uppercase">{hslToHex(activeForeground)}</span>
                                    </div>
                                </div>
                                <div className="flex-1 p-3 rounded-xl border border-white/10 bg-slate-800/20">
                                    <p style={{ color: `hsl(${activeForeground})` }} className="text-sm font-medium">Aperçu du texte : La lecture est une porte ouverte sur un monde enchanté.</p>
                                </div>
                            </div>
                        </div>

                        {/* Muted Background Color */}
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">Couleur Muted (Arrière-plans secondaires)</label>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-xl border border-white/10">
                                    <input
                                        type="color"
                                        value={hslToHex(activeMuted)}
                                        onChange={(e) => handleMutedChange(hexToHSL(e.target.value))}
                                        className="w-10 h-10 bg-transparent cursor-pointer rounded overflow-hidden p-0 border-none"
                                    />
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-white">Couleur personnalisée</span>
                                        <span className="text-xs text-slate-500 uppercase">{hslToHex(activeMuted)}</span>
                                    </div>
                                </div>
                                <div className="flex-1 p-3 rounded-xl border border-white/10" style={{ backgroundColor: `hsl(${activeMuted})` }}>
                                    <p style={{ color: `hsl(${activeForeground})` }} className="text-sm font-medium">Aperçu : Zone Muted</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Email Template & Loan Delay Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500">
                            <Mail size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Template d'email & Délai</h2>
                            <p className="text-sm text-slate-400">Message automatique pour les retards et durée d'emprunt</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-xs text-amber-200">
                            <strong>Note :</strong> Vous pouvez utiliser les variables <code>{"{name}"}</code> pour le nom du membre et <code>{"{title}"}</code> pour le titre du livre.
                        </div>

                        <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4 space-y-3">
                            <label className="block text-sm font-medium text-slate-400">Délai d'emprunt (en jours)</label>
                            <input
                                type="number"
                                min="1"
                                value={loanDelay}
                                onChange={(e) => setLoanDelay(parseInt(e.target.value, 10))}
                                className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                            />
                        </div>

                        <textarea
                            value={emailContent}
                            onChange={(e) => setEmailContent(e.target.value)}
                            className="w-full h-48 bg-slate-800/50 border border-white/10 rounded-xl p-4 text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 font-sans leading-relaxed"
                            placeholder="Écrivez votre template ici..."
                        />
                    </div>
                </div>

                {/* Library Information Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                            <Monitor size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Informations de la bibliothèque</h2>
                            <p className="text-sm text-slate-400">Ces informations seront visibles par les membres (icône cloche)</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-400">Adresse</label>
                                <input
                                    type="text"
                                    value={libAddress}
                                    onChange={(e) => setLibAddress(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-white/10 rounded-xl px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-400">Email de contact</label>
                                <input
                                    type="email"
                                    value={libEmail}
                                    onChange={(e) => setLibEmail(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-white/10 rounded-xl px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-400">Téléphone</label>
                                <input
                                    type="text"
                                    value={libPhone}
                                    onChange={(e) => setLibPhone(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-white/10 rounded-xl px-4 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                            <div className="space-y-4">
                                <label className="block text-sm font-medium text-slate-400">Logo de la bibliothèque</label>
                                <div className="flex items-center gap-6">
                                    <div className="w-24 h-24 rounded-2xl bg-slate-800/50 border border-white/10 flex items-center justify-center overflow-hidden">
                                        {libLogo ? (
                                            <img src={libLogo} alt="Logo" className="max-w-full max-h-full object-contain" />
                                        ) : (
                                            <Upload size={32} className="text-slate-500" />
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-medium transition-colors"
                                        >
                                            <Upload size={16} /> Uploader le logo
                                        </button>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="block text-sm font-medium text-slate-400 flex items-center gap-2">
                                <Clock size={16} /> Horaires d'ouverture
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white/5 p-4 rounded-xl border border-white/5">
                                {libHours.map((day, idx) => (
                                    <div key={day.days} className="flex items-center gap-3">
                                        <span className="w-20 text-xs font-bold text-slate-500 uppercase">{day.days}</span>
                                        <input
                                            type="text"
                                            value={day.hours}
                                            onChange={(e) => {
                                                const newHours = [...libHours];
                                                newHours[idx].hours = e.target.value;
                                                setLibHours(newHours);
                                            }}
                                            className="flex-1 bg-slate-900/50 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-500">
                            <Sliders size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Fonctionnalités</h2>
                            <p className="text-sm text-slate-400">Activez ou désactivez certaines fonctionnalités de votre établissement</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between bg-slate-800/50 border border-white/10 rounded-xl p-4">
                            <div>
                                <h3 className="text-white font-medium">Réservation de livres</h3>
                                <p className="text-sm text-slate-400">Autoriser les membres à réserver des livres en ligne</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={enableReservation}
                                    onChange={(e) => setEnableReservation(e.target.checked)}
                                />
                                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Biblius Keywords Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400">
                            <Tag size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Mots-clés Biblius</h2>
                            <p className="text-sm text-slate-400">Rappel des mots-clés pour la recherche avancée</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                                <code className="bg-primary/20 text-primary px-2 py-1 rounded text-sm font-mono">biblius:multiples</code>
                            </div>
                            <p className="text-slate-300 text-sm">
                                Affiche tous les livres qui ont des exemplaires multiples (plusieurs livres pour la même œuvre).
                            </p>
                        </div>
                    </div>
                </div>

                {/* CSV Template Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                            <Download size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Importation des membres</h2>
                            <p className="text-sm text-slate-400">Téléchargez le modèle CSV pour importer vos membres en masse</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-slate-800/50 border border-white/10 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-center sm:text-left">
                                <h3 className="text-white font-medium mb-1">Modèle d'importation CSV</h3>
                                <p className="text-sm text-slate-400">Ce fichier contient les colonnes nécessaires (nom, prénom, email, role, tel, classe) pour un import réussi.</p>
                                <p className="text-sm text-slate-400">Les emails sont en minuscules obligatoirement</p>
                                <p className="text-sm text-slate-400">Le type d'utilisateur est Étudiant ou Enseignant (Il faut respecter la première lettre en majuscule (et accentué si besoin) du type puis le reste en minuscule)</p>
                                <p className="text-sm text-slate-400">Ouvrir Excel et choisir d'importer les données depuis un fichier texte, le format d'encodage du fichier est <strong>UTF-8</strong></p>

                            </div>
                            <button
                                onClick={handleDownloadTemplate}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95 whitespace-nowrap"
                            >
                                <Download size={20} />
                                Télécharger le template
                            </button>
                        </div>
                    </div>
                </div>


                {/* Appearance Mode */}
                <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            <Monitor size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Mode d'apparence</h2>
                            <p className="text-sm text-slate-400">Choisissez entre mode clair et sombre</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => handleThemeModeChange('light')}
                            className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${themeMode === 'light'
                                ? 'bg-primary/10 border-primary text-white font-bold'
                                : 'bg-muted/50 border-border text-slate-400 hover:border-primary/50'
                                }`}
                        >
                            <Sun size={24} />
                            <span className="font-medium">Clair</span>
                        </button>
                        <button
                            onClick={() => handleThemeModeChange('dark')}
                            className={`p-4 rounded-xl border transition-all flex flex-col items-center gap-2 ${themeMode === 'dark'
                                ? 'bg-primary/10 border-primary text-white font-bold'
                                : 'bg-muted/50 border-border text-slate-400 hover:border-primary/50'
                                }`}
                        >
                            <Moon size={24} />
                            <span className="font-medium">Sombre</span>
                        </button>
                    </div>
                </div>
            </motion.div>

            <div className="fixed bottom-6 right-8">
                <button
                    onClick={handleSaveSettings}
                    className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-xl shadow-emerald-500/20 active:scale-95"
                >
                    <Save size={20} />
                    Tout sauvegarder
                </button>
            </div>
        </div>
    );
}
